<template>
  <div class="contact">
    <div class="contact__body">
      <!-- Header: hero icon inside a soft pink halo, then eyebrow +
           title + subdesc. Matches the tribute-swing hero cadence
           (size, halo, type scale) so the final "arrival" step feels
           as deliberate as the dedication moment. The rotating dashed
           ring stays tribute-only — it's the signature for that
           pivotal decision; we don't dilute it here. -->
      <div class="contact__header">
        <span class="contact__halo" aria-hidden="true">
          <span class="contact__halo-ring" aria-hidden="true" />
          <span class="contact__icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
        </span>
        <p class="contact__eyebrow">Almost there</p>
        <h2 class="contact__title">Your details</h2>
        <p class="contact__desc">Where should we send your tax receipt?</p>
      </div>

      <!-- Fields grid -->
      <div class="contact__fields">
        <div class="contact__row contact__row--2">
          <div class="contact__field">
            <label for="firstName" class="contact__label">First name</label>
            <input
              type="text"
              class="contact__input"
              :class="{ 'has-error': errors.includes('firstName') }"
              v-model="donationWidgetState.firstName"
              id="firstName"
              name="firstName"
              autocomplete="given-name"
            >
          </div>
          <div class="contact__field">
            <label for="lastName" class="contact__label">Last name</label>
            <input
              type="text"
              class="contact__input"
              :class="{ 'has-error': errors.includes('lastName') }"
              v-model="donationWidgetState.lastName"
              id="lastName"
              name="lastName"
              autocomplete="family-name"
            >
          </div>
        </div>

        <div class="contact__field">
          <label for="email" class="contact__label">Email address</label>
          <input
            type="email"
            class="contact__input"
            :class="{ 'has-error': errors.includes('email') }"
            v-model="donationWidgetState.email"
            id="email"
            name="email"
            autocomplete="email"
            inputmode="email"
          >
        </div>

        <div class="contact__row contact__row--city">
          <div class="contact__field contact__field--city">
            <label for="city" class="contact__label">City</label>
            <input
              type="text"
              class="contact__input"
              :class="{ 'has-error': errors.includes('city') }"
              v-model="donationWidgetState.city"
              id="city"
              name="city"
              autocomplete="address-level2"
            >
          </div>
          <div class="contact__field contact__field--state">
            <label for="state" class="contact__label">State</label>
            <div class="contact__select-wrap">
              <select
                class="contact__input contact__select"
                :class="{ 'has-error': errors.includes('state') }"
                id="state"
                v-model="donationWidgetState.state"
                name="state"
                autocomplete="address-level1"
              >
                <option v-for="option in options" :key="option" :value="option">{{ option }}</option>
              </select>
              <svg class="contact__select-chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
          <div class="contact__field contact__field--zip">
            <label for="zip" class="contact__label">ZIP</label>
            <input
              v-maska data-maska="#####"
              type="text"
              class="contact__input"
              :class="{ 'has-error': errors.includes('zip') }"
              v-model="donationWidgetState.zip"
              id="zip"
              name="zip"
              inputmode="numeric"
              autocomplete="postal-code"
            >
          </div>
        </div>

        <!-- Anonymous checkbox hidden when a tribute is in play (the
             tribute implies a named dedication). -->
        <label v-show="!donationWidgetState.hasDedication" class="contact__checkbox">
          <input
            id="anonymousDonation"
            type="checkbox"
            v-model="donationWidgetState.isAnonymous"
          >
          <span class="contact__checkbox-box" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 8.5 6.5 12 13 5"/>
            </svg>
          </span>
          <span class="contact__checkbox-label">Make this donation anonymous</span>
        </label>
      </div>
    </div>

    <!-- Actions — horizontal row per image1: gray-outlined BACK
         pill on the left, primary DONATE button on the right. -->
    <div class="contact__actions">
      <button
        @click.prevent="back"
        class="dw-back-pill dw-back-pill--lg"
        type="button"
        aria-label="Back to previous step"
      >
        <svg class="dw-back-pill__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
        <span>Back</span>
      </button>
      <button
        @click.prevent="next"
        class="contact__submit"
        :disabled="donationWidgetState.isProcessing"
      >
        <svg v-if="donationWidgetState.isProcessing" class="contact__spinner" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="contact__spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none"/>
          <path class="contact__spinner-head" d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round"/>
        </svg>
        <span v-if="donationWidgetState.isProcessing">Processing…</span>
        <span v-else>Donate ${{ donationAmountForView }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import BigNumber from 'bignumber.js'
  import { vMaska } from "maska"
  import mitt from 'mitt';
  
  const emitter = mitt();

  export default {
    name: 'contact-form',
    directives: { maska: vMaska },
    mounted() {
      // Auto-focus the first name input ONCE the slide-in transition
      // has fully settled. $nextTick alone fires mid-transition (~16ms)
      // which triggers the browser's focus-scroll behavior while the
      // fieldset is still translating — visible as a "jumpy / fast"
      // entrance. Waiting ~560ms matches the 520ms fieldset slide +
      // one frame for layout to stabilize. preventScroll=true is a
      // secondary guard for Chromium's focus-scroll heuristic.
      this._focusTimer = setTimeout(() => {
        const el = document.getElementById('firstName')
        if (el && typeof el.focus === 'function') {
          el.focus({ preventScroll: true })
        }
      }, 560)
    },

    beforeUnmount() {
      if (this._focusTimer) clearTimeout(this._focusTimer)
    },
    data() {
      return {
        errors: [],
        options: [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'FM', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ],
      }
    },

    computed: {
      calculatedFee() {
        const fixedFee = '30' // in cents
        const percentageFee = new BigNumber(0.022) // percentage
        const one = new BigNumber(1)
        const goal = new BigNumber(this.donationWidgetState.amount)
        const calculatedCharge = goal.plus(fixedFee).dividedBy(one.minus(percentageFee))
        return BigNumber(calculatedCharge.minus(goal))
      },
      
      calculatedTotal() {
        const amount = new BigNumber(this.donationWidgetState.amount)
        if (this.donationWidgetState.paidForTransactionFee) {
          return BigNumber(amount.plus(this.calculatedFee))
        }
        return amount
      },
      donationWidgetState() {
        return this.$store.getters.donationWidget
      },
      donationAmountForView() {
        return new BigNumber(this.calculatedTotal).dividedBy(100).toFormat(2)
      },
    },

    methods: {
      back() {
        this.donationWidgetState.enterFromDirection = '-translate-x-full'
        this.donationWidgetState.leaveToDirection = 'translate-x-full'

        this.donationWidgetState.steps.contact.isInView = false
        // Go back to the employer-match step (added between payment and
        // contact). If for any reason that step was never made available
        // (legacy store pre-migration), fall back to payment.
        if (this.donationWidgetState.steps.employerMatch) {
          this.donationWidgetState.steps.employerMatch.isInView = true
        } else {
          this.donationWidgetState.steps.payment.isInView = true
        }
        return
      },
      async next() {
        if (this.donationWidgetState.isProcessing) {
          return;
        }

        this.donationWidgetState.isProcessing = true;
        this.errors = [];

        // Collect-all validation: build the full error list first, surface
        // every invalid field in one notification. Previously bailed on
        // the first failure, forcing multiple submit clicks to find every
        // missing field.
        if (!this.donationWidgetState.firstName) this.errors.push('firstName')
        if (!this.donationWidgetState.lastName)  this.errors.push('lastName')
        if (!this.donationWidgetState.email || !this.validEmail()) this.errors.push('email')
        if (!this.donationWidgetState.city) this.errors.push('city')
        if (!this.donationWidgetState.state) this.errors.push('state')
        const zipCodeRegex = /^\d{5}$/
        if (!this.donationWidgetState.zip || !zipCodeRegex.test(this.donationWidgetState.zip)) {
          this.errors.push('zip')
        }

        if (this.errors.length > 0) {
          const labels = {
            firstName: 'first name',
            lastName: 'last name',
            email: 'a valid email',
            city: 'city',
            state: 'state',
            zip: 'a 5-digit ZIP',
          }
          const missing = this.errors.map(k => labels[k]).filter(Boolean)
          const text = missing.length === 1
            ? `Please enter ${missing[0]}`
            : `Please complete: ${missing.join(', ')}`
          this.$notify({ text, type: 'error', group: 'donationWidget' })
          this.donationWidgetState.isProcessing = false
          return
        }

        if (this.errors.length === 0) {
          try {
            const response = await axios.post('/api/donation-widget-payment', {
              team_id: 1,
              campaign_id: this.donationWidgetState.campaignId,
              pledge_id: this.donationWidgetState.pledgeId,
              amount: new BigNumber(this.calculatedTotal).toFixed(0),
              calculated_transaction_fee: this.calculatedFee.toFixed(0),
              paid_for_transaction_fee: this.donationWidgetState.paidForTransactionFee,
              payment_type_id: 1,
              payment_type_identifier: this.donationWidgetState.paymentIdentifier,
              business_name: this.donationWidgetState.businessName,
              address: '',
              is_anonymous: this.donationWidgetState.isAnonymous,
              occurrence: this.donationWidgetState.occurrence,
              double_the_donation: this.donationWidgetState.doubleTheDonation,
              token: this.donationWidgetState.paymentToken,
              has_dedication: this.donationWidgetState.hasDedication,
              in_memory_honor_of: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeName : null,
              honoree_name: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeName : null,
              honoree_email: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeEmail : null,
              message_to_recipient: this.donationWidgetState.hasDedication ? this.donationWidgetState.honoreeMessage : null,
              selection: this.donationWidgetState.hasDedication ? this.donationWidgetState.inHonorOfSelection : null,
              first_name: this.donationWidgetState.firstName,
              last_name: this.donationWidgetState.lastName,
              email: this.donationWidgetState.email,
              city: this.donationWidgetState.city,
              state: this.donationWidgetState.state,
              zip: this.donationWidgetState.zip
            });
          
            // Handle success
            emitter.emit('campaign-donation-update', response.data.campaign);
            this.donationWidgetState.successfulTransactionData = response.data;
          
            this.donationWidgetState.steps.contact.isInView = false;
            this.donationWidgetState.steps.thankYou.isInView = true;
          } catch (reason) {
            console.error('Payment submission failed:', reason);

            // Extract error message - handle multiple response formats from backend
            let errorMessage = 'An error occurred while processing your donation. Please try again.';
            if (reason.response?.data?.errors?.error?.[0]) {
              // Custom error format from MakeDonationController catch block (line 169)
              errorMessage = reason.response.data.errors.error[0];
            } else if (reason.response?.data?.error) {
              // Legacy error format (may exist in older API responses)
              errorMessage = reason.response.data.error;
            } else if (reason.response?.data?.message) {
              // Laravel exception format (500 errors, rate limits from Handler.php)
              errorMessage = reason.response.data.message;
            } else if (reason.message) {
              // JavaScript Error object (network errors, etc.)
              errorMessage = reason.message;
            }

            this.$notify({
              text: errorMessage,
              type: 'error',
              group: 'donationWidget',
            });

            // Clear payment token after failure. Stripe PaymentMethods are consumed when
            // attached to a PaymentIntent (MakeDonationController.php:445). After a failed
            // attempt, the token cannot be reused - user must enter fresh card details.
            this.donationWidgetState.paymentToken = null;
            this.donationWidgetState.paymentIdentifier = null;

            // Navigate back to payment step so user can re-enter card details
            this.donationWidgetState.steps.contact.isInView = false;
            this.donationWidgetState.steps.payment.isInView = true;

            this.donationWidgetState.isProcessing = false;
            return;
          }
        }
        
        // Only set isProcessing to false if we've reached this point (successful donation)
        this.donationWidgetState.isProcessing = false;
      },
      validEmail() {
        var re = /\S+@\S+\.\S+/;
        return re.test(this.donationWidgetState.email);
      },
    },
  }
</script>

<style scoped>
/* ─── Layout ──────────────────────────────────────────────── */
.contact {
  /* Vertical rhythm per image1: a small lift above the avatar (top
     padding) plus deliberate breathing room BELOW the BACK/DONATE
     action row so the buttons don't hug the panel's bottom edge.
     .contact__actions has margin-top:auto so it sits at the bottom
     of this column — increasing the bottom padding here is what
     creates the gap under the buttons. */
  /* Per image1: avatar tight to the top, then form/checkbox, then
     a deliberate visual gap above the BACK+DONATE row, then a
     small bottom padding (so the buttons don't pin to the very
     bottom edge). The gap above the actions is created by
     margin-top on .contact__actions (not margin-top:auto), and
     body gap is tightened to make the math fit the 484px panel. */
  padding: 0 1.75rem 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: 'Red Hat Display', system-ui, sans-serif;
}
.contact__body {
  display: flex;
  flex-direction: column;
  /* Tightened to 6px to make room for the explicit gap above the
     action row (per image1's vertical rhythm) within the 484px
     panel constraint. */
  gap: 6px;
}

/* ─── Header ─────────────────────────────────────────────── */
.contact__header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 0 8px;
}
.contact__halo {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin-bottom: 0;
}
.contact__halo-ring {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
}
.contact__icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  color: #ffffff;
  background: #DA1884;
}
.contact__icon svg {
  width: 24px;
  height: 24px;
}
.contact__eyebrow {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.8125rem;         /* 13px */
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #DA1884;
  margin: 2px 0 0;
  line-height: 1;
}
.contact__title {
  margin: 4px 0 4px;
  font-size: 1.5rem;            /* 24px */
  font-weight: 800;
  color: #1d2549;
  line-height: 1.1;
  letter-spacing: -0.015em;
}
.contact__desc {
  margin: 16px auto;
  max-width: 32ch;
  font-size: 1rem;              /* 16px (was 14px) — receipt instruction, mobile legibility (client 2026-05-29) */
  color: #6B7280;
  line-height: 1.45;
}

/* ─── Fields ─────────────────────────────────────────────── */
.contact__fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.contact__row {
  display: grid;
  gap: 10px;
}
.contact__row--2    { grid-template-columns: 1fr 1fr; }
/* City wider than State, ZIP balanced for a cleaner column ratio. */
.contact__row--city { grid-template-columns: 1.1fr 0.75fr 0.85fr; }

.contact__field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }

.contact__label {
  font-size: 0.6875rem;         /* 11px — was 10px, matched to trust-panel eyebrow */
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6B7280;
  line-height: 1;
}
.contact__input {
  width: 100%;
  height: 37px;
  padding: 8px 12px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  line-height: 19px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 9px;
  outline: none;
  /* Keep the field's scroll-margin within the widget large enough
     that any focus-into-view scroll (Safari + some Chromium cases)
     doesn't nudge the form up when the label is right near the top
     of the visible area. */
  scroll-margin: 40px;
  /* Border transitions in; box-shadow snap-in (instant). Previously
     the 3px pink ring grew-in over 200ms which read as a subtle
     upward "shift" as the ring expanded past the label above. */
  transition:
    border-color 160ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 180ms;
}
.contact__input::placeholder { color: #9CA3AF; }
.contact__input:hover {
  border-color: #D1D5DB;        /* gray-300 */
}
.contact__input:focus {
  border-color: #F0A3CE;        /* pink-300 */
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.contact__input.has-error {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
  background: #FEF2F2;
}

/* Select: strip browser chevron, supply our own so the state
   dropdown matches the inputs visually. */
.contact__select-wrap { position: relative; }
.contact__select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;          /* leaves room for the chevron */
  cursor: pointer;
}
.contact__select::-ms-expand { display: none; }
.contact__select-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6B7280;
  pointer-events: none;
  transition: color 180ms;
}
.contact__select:focus + .contact__select-chevron { color: #DA1884; }

/* ─── Checkbox (anonymous) ───────────────────────────────── */
.contact__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 2px 0 0;
  user-select: none;
}
.contact__checkbox input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.contact__checkbox-box {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #D1D5DB;
  border-radius: 5px;
  background: #ffffff;
  color: transparent;
  transition:
    background-color 180ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 180ms cubic-bezier(0.22, 1, 0.36, 1),
    color 180ms;
}
.contact__checkbox input:checked + .contact__checkbox-box {
  background: #DA1884;
  border-color: #DA1884;
  color: #ffffff;
}
.contact__checkbox input:focus-visible + .contact__checkbox-box {
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.2);
}
.contact__checkbox:hover .contact__checkbox-box {
  border-color: #F0A3CE;
}
.contact__checkbox-label {
  font-size: 15px; /* was 13px — anonymous-donation option, mobile legibility */
  color: #374151;
}

/* ─── Actions ──────────────────────────────────────────────
   Horizontal row per image1: gray-outlined BACK pill on the
   left (compact, intrinsic width) + primary DONATE button on
   the right (flex-fills the rest of the row). The 1.5rem
   margin-top creates the deliberate vertical gap above the
   row (between the anonymous checkbox and the buttons) that
   image1 calls out — replaces the prior margin-top:auto which
   pinned the row to the panel bottom. */
.contact__actions {
  margin-top: 1.5rem;
  padding-top: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.contact__submit {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 20px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ffffff;
  background: #DA1884;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 180ms;
}
.contact__submit:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.03);
}
.contact__submit:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}
.contact__submit:focus-visible {
  outline: 2px solid #A41263;
  outline-offset: 2px;
}
.contact__submit:disabled {
  cursor: wait;
  opacity: 0.75;
  transform: none;
}

/* Inline SVG spinner — matches PaymentForm's processing state. */
.contact__spinner {
  width: 16px;
  height: 16px;
  animation: contact-spin 0.9s linear infinite;
}
.contact__spinner-track { opacity: 0.25; }
.contact__spinner-head  { opacity: 0.95; }
@keyframes contact-spin {
  to { transform: rotate(360deg); }
}

/* Step 4's back affordance uses the global .dw-back-pill defined
   in MakeDonationWidget.vue. Action-row layout (back left, donate
   right, flex-fills) is owned here — see .contact__actions. */

@media (prefers-reduced-motion: reduce) {
  .contact__submit,
  .contact__input,
  .contact__checkbox-box { transition-duration: 80ms; }
  .contact__spinner { animation-duration: 1.8s; }
}

</style>
